<template>
  <ui-dialog
    :show="true"
    :max-width="panelsVisibility.right ? '1120px' : '960px'"
    :closable="false"
    :loading="loading"
    :title="$t('message.reviews.filter')"
  >
    <v-row v-if="!loading" class="fill-height" no-gutters>
      <v-col
        :cols="panelsColsSpan.left"
        class="pr-6 d-flex flex-column max-height-100"
        :class="{
          'single-panel': !panelsVisibility.middle && !panelsVisibility.right
        }"
      >
        <div v-if="showOpenStatus || showLocationStatus" class="statuses">
          <v-select
            v-if="showLocationStatus"
            v-model="status.location"
            :label="$t('message.common.facilitiesStatusInGoogleMaps')"
            :items="availableStatuses.location"
            :menu-props="{ bottom: true, offsetY: true }"
            hide-details
            class="statuses__select"
          >
            <template slot="selection" slot-scope="data">
              {{ $t(`message.facilityStatusTypes.${data.item}`) }}
            </template>
            <template slot="item" slot-scope="data">
              {{ $t(`message.facilityStatusTypes.${data.item}`) }}
            </template></v-select
          >
          <v-select
            v-if="showBingLocationStatus"
            v-model="status.bing"
            :label="$t('message.common.facilitiesStatusInBingMaps')"
            :items="availableStatuses.bing"
            :menu-props="{ bottom: true, offsetY: true }"
            hide-details
            class="statuses__select"
          >
            <template slot="selection" slot-scope="data">
              {{ $t(`message.bingFacilityStatusTypes.${data.item}`) }}
            </template>
            <template slot="item" slot-scope="data">
              {{ $t(`message.bingFacilityStatusTypes.${data.item}`) }}
            </template></v-select
          >
          <v-select
            v-if="showOpenStatus"
            v-model="status.open"
            :label="$t('message.common.facilityOpenStatus')"
            :items="availableStatuses.open"
            :menu-props="{ bottom: true, offsetY: true }"
            hide-details
            class="statuses__select"
            @change="handleLocationStatusChange"
          >
            <template slot="selection" slot-scope="data">
              {{ $t(`message.openStatusTypes.${data.item}`) }}
            </template>
            <template slot="item" slot-scope="data">
              {{ $t(`message.openStatusTypes.${data.item}`) }}
            </template>
          </v-select>
        </div>
        <div>
          <v-radio-group v-model="searchType" color="primary" row>
            <v-radio
              v-if="showFacilities"
              :label="$t('message.reviews.facilities')"
              :value="searchTypes.FACILITIES"
            ></v-radio>
            <v-radio
              v-if="showCities"
              :label="$t('message.reviews.cities')"
              :value="searchTypes.CITY"
            ></v-radio>
            <v-radio
              v-if="showFacilitiesGroups && allFacilitiesAccess"
              :label="$t('message.reviews.facilitiesGroups')"
              :value="searchTypes.FACILITIES_GROUPS"
            ></v-radio>
          </v-radio-group>
        </div>
        <PostFacilitiesAlert
          v-if="showAlert && getAlertType"
          class="mb-6"
          text-bold
          :type="getAlertType"
        />
        <div class="list-wrapper">
          <SectionLoader
            :status="loaders.groups || loaders.cities || loaders.facilities"
            :opacity="1"
          />
          <div class="d-flex align-center full-width mb-4">
            <v-text-field
              v-if="searchType === searchTypes.FACILITIES"
              v-model="facilitiesList.searchInputValue"
              :placeholder="$t('message.reviews.search')"
              prepend-inner-icon="icon-search"
              clearable
              outlined
              rounded
              hide-details
              @input="onFacilitySearch"
            ></v-text-field>
            <v-text-field
              v-else-if="searchType === searchTypes.CITY"
              v-model="citiesSearchInputValue"
              :placeholder="$t('message.reviews.searchCity')"
              prepend-inner-icon="icon-search"
              clearable
              outlined
              rounded
              hide-details
            ></v-text-field>
          </div>
          <v-row
            no-gutters
            v-if="
              searchType === searchTypes.FACILITIES &&
                facilitiesMixin__currentPageFacilities.length > 0
            "
            class="align-center mb-3"
          >
            <v-col>
              <Pagination
                class="flex-grow-1"
                key="pagination-facilities"
                :page="facilitiesList.page"
                :per-page="facilitiesList.perPage"
                :options="facilitiesList.perPageOptions"
                :total-count="facilitiesMixin__totalCount"
                @change="handleFacilitiesPaginationChange"
              ></Pagination>
            </v-col>
          </v-row>
          <v-row
            no-gutters
            v-else-if="
              searchType === searchTypes.FACILITIES_GROUPS &&
                currentPageFacilitiesGroups.length > 0
            "
            class="align-center mb-3"
          >
            <v-col>
              <Pagination
                class="flex-grow-1"
                key="pagination-facilities-groups"
                :page="facilitiesGroupsList.page"
                :per-page="facilitiesGroupsList.perPage"
                :options="facilitiesGroupsList.perPageOptions"
                :total-count="facilitiesGroupsTotalCount"
                @change="handleFacilitiesGroupsPaginationChange"
              ></Pagination>
            </v-col>
          </v-row>

          <div class="d-flex flex-column overflow-y-auto list-container">
            <div class="max-height-100">
              <template
                v-if="
                  facilitiesMixin__currentPageFacilities.length > 0 &&
                    searchType === searchTypes.FACILITIES
                "
              >
                <v-list-item
                  ripple
                  class="list-item list-item--all"
                  @click="handleToggleAllFacilities"
                >
                  <v-checkbox
                    @click.prevent=""
                    :inputValue="facilitiesMixin__isAllFacilitiesSelected"
                  ></v-checkbox>
                  <v-list-item-content>
                    <v-list-item-title>
                      <p class="list-item__name mb-0">
                        {{ $t("message.common.selectAll") }}
                      </p>
                    </v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action-text>
                    <ItemsCounter
                      v-if="facilitiesMixin__selectedFacilitiesCount"
                      :count="facilitiesMixin__selectedFacilitiesCount"
                    />
                  </v-list-item-action-text>
                </v-list-item>
                <v-virtual-scroll
                  :items="facilitiesMixin__currentPageFacilities"
                  :item-height="listItemHeight"
                  :height="listsHeights.facilities"
                  class="wizard__list"
                >
                  <template v-slot:default="{ item }">
                    <v-list-item
                      :key="item.id"
                      ripple
                      class="list-item"
                      @click="
                        !isItemDisabled(item)
                          ? handleToggleFacility(item)
                          : null
                      "
                    >
                      <v-tooltip
                        :disabled="!isItemDisabled(item)"
                        max-width="300px"
                        bottom
                      >
                        <template v-slot:activator="{ on }">
                          <span v-on="on">
                            <v-checkbox
                              :disabled="isItemDisabled(item)"
                              :inputValue="isFacilitySelected(item)"
                            >
                            </v-checkbox>
                          </span>
                        </template>
                        <span>{{
                          $t("message.common.areaFacilityTooltip")
                        }}</span>
                      </v-tooltip>
                      <v-list-item-content>
                        <v-list-item-title>
                          <p class="list-item__name">{{ item.name }}</p>
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          <p
                            class="list-item__description"
                            :class="{
                              'list-item__description--is-store-code':
                                item.storeCode
                            }"
                          >
                            <span>{{ item.formattedAddress }} </span>
                            <span v-if="item.storeCode">
                              {{ $t("message.common.storeCode") }}:
                              {{ item.storeCode }}</span
                            >
                          </p>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-virtual-scroll>
              </template>
              <template
                v-else-if="
                  filteredCities.length && searchType === searchTypes.CITY
                "
              >
                <v-list-item
                  ripple
                  class="list-item list-item--all"
                  @click="handleSelectAllCities(!isAllCitiesSelected)"
                >
                  <v-checkbox
                    @click.prevent=""
                    :inputValue="isAllCitiesSelected"
                  />
                  <v-list-item-content>
                    <v-list-item-title>
                      <p class="list-item__name mb-0">
                        {{ $t("message.common.selectAll") }}
                      </p>
                    </v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action-text>
                    <ItemsCounter
                      v-if="citiesChecked.length"
                      :count="citiesChecked.length"
                    />
                  </v-list-item-action-text>
                </v-list-item>
                <v-virtual-scroll
                  :items="filteredCities"
                  :item-height="listItemHeight"
                  :height="listsHeights.cities"
                  class="wizard__list"
                >
                  <template v-slot:default="{ item }">
                    <v-list-item
                      :key="item"
                      ripple
                      class="list-item"
                      @click="toggleSelectedCity(item)"
                    >
                      <v-checkbox
                        :inputValue="citiesChecked.includes(item)"
                      ></v-checkbox>
                      <v-list-item-content>
                        <v-list-item-title>
                          <p class="list-item__name mb-0">{{ item }}</p>
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-virtual-scroll>
              </template>

              <template
                v-else-if="
                  filteredFacilitiesGroups.length &&
                    searchType === searchTypes.FACILITIES_GROUPS
                "
              >
                <v-list-item
                  ripple
                  class="list-item list-item--all"
                  @click="
                    handleToggleAllFacilitiesGroups(
                      !isAllFacilitiesGroupsSelected
                    )
                  "
                >
                  <v-checkbox
                    @click.prevent=""
                    :inputValue="isAllFacilitiesGroupsSelected"
                  />
                  <v-list-item-content>
                    <v-list-item-title>
                      <p class="list-item__name mb-0">
                        {{ $t("message.common.selectAll") }}
                      </p>
                    </v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action-text>
                    <ItemsCounter
                      v-if="selectedFacilitiesGroupsCount"
                      :count="selectedFacilitiesGroupsCount"
                    />
                  </v-list-item-action-text>
                </v-list-item>
                <v-virtual-scroll
                  :items="filteredFacilitiesGroups"
                  :item-height="listItemHeight"
                  :height="listsHeights.facilitiesGroups"
                  class="wizard__list"
                >
                  <template v-slot:default="{ item }">
                    <v-list-item
                      :key="item.id"
                      ripple
                      class="list-item"
                      @click="handleToggleFacilitiesGroup(item.id, item.name)"
                    >
                      <v-checkbox
                        :inputValue="checkIsGroupSelected(item)"
                      ></v-checkbox>
                      <v-list-item-content>
                        <v-list-item-title class="d-flex align-center">
                          <p class="list-item__name mb-0">{{ item.name }}</p>
                          <p class="list-item__facilities-count">
                            {{ getFacilitiesCountLabel(item.facilities_count) }}
                          </p>
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-virtual-scroll>
              </template>
              <no-data v-else></no-data>
              <SectionLoader :status="loadingList" />
            </div>
          </div>
        </div>
      </v-col>
      <v-col
        v-if="panelsVisibility.middle"
        :cols="panelsColsSpan.middle"
        class="border-l-primary pl-4"
      >
        <template v-if="showRatings">
          <h2 class="col-header pl-4">{{ $t("message.reviews.rating") }}</h2>
          <v-list class="overflow-y-auto">
            <v-list-item
              @click="onAllRatingsSelectChange(ratingChecked.length !== 5)"
              ripple
            >
              <v-checkbox
                class="mt-0 pt-0"
                :hideDetails="true"
                :inputValue="ratingChecked.length === 5"
              />
              <v-list-item-content>
                <v-list-item-title>
                  <p class="list-item__name">
                    {{ $t("message.common.selectAll") }}
                  </p>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-for="item in 5"
              :key="6 - item"
              ripple
              class="list-item"
              @click="toggleSelectedRating(6 - item)"
            >
              <v-checkbox
                class="mt-0 pt-0"
                :hideDetails="true"
                :inputValue="ratingChecked.includes(6 - item)"
              ></v-checkbox>
              <v-list-item-content>
                <v-list-item-title>
                  <StarsRating :rating="6 - item" />
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </template>

        <div class="reviews" v-if="showCommentsOptions">
          <h3 class="">{{ $t("message.reviews.opinions") }}</h3>
          <ReviewsCheckbox
            :input-value="onlyComments === true"
            :text="$t('message.reviews.onlyComments')"
            @click="onlyComments = onlyComments === true ? null : true"
          />
          <ReviewsCheckbox
            :input-value="onlyComments === false"
            :text="$t('message.reviews.onlyNoComments')"
            @click="onlyComments = onlyComments === false ? null : false"
          />
          <ReviewsCheckbox
            v-if="showAccurateReviewsFilter"
            :input-value="onlyAccurate"
            :text="$t('message.reviews.onlyAccurate')"
            @click="onlyAccurate = !onlyAccurate"
          />
          <ReviewsCheckbox
            v-if="showHasNote"
            :input-value="onlyNotes"
            :text="$t('message.reviews.onlyNotes')"
            @click="onlyNotes = !onlyNotes"
          />
          <ReviewsCheckbox
            v-if="showDeletedOption"
            :input-value="showDeleted"
            :text="$t('message.reviews.showDeletedReviews')"
            @click="showDeleted = !showDeleted"
          />
          <template v-if="showDateOptions">
            <ReviewsCheckbox
              :input-value="onlyCreated"
              :text="$t('message.reviews.onlyCreated')"
              @click="handleOnlyCreated"
            />
            <ReviewsCheckbox
              :input-value="onlyUpdated"
              :text="$t('message.reviews.onlyUpdated')"
              @click="handleOnlyUpdated"
            />
          </template>
          <template v-if="showReviewBoosterFilters">
            <ReviewsCheckbox
              :input-value="addedToGoogle"
              :text="$t('message.reviews.addedToGoogle')"
              @click="handleAddedToGoogle"
            />
            <ReviewsCheckbox
              :input-value="writtenWithAI"
              :text="$t('message.reviews.writtenWithAI')"
              @click="handleWrittenWithAI"
            />
          </template>
        </div>
        <div class="reviews" v-if="showReviewBoosterFilters">
          <h3 class="">{{ $t("message.reviews.actions") }}</h3>
          <ReviewsCheckbox
            :input-value="googleMapsClicked"
            :text="$t('message.reviewBooster.reviews.googleMapsClicked')"
            @click="handleGoogleMapsClicked"
          />
        </div>
      </v-col>
      <v-col
        v-if="panelsVisibility.right"
        :cols="panelsColsSpan.right"
        class="border-l-primary pl-4"
      >
        <header class="pl-4">
          <h2 class="col-header">{{ $t("message.reviews.tags.tags") }}</h2>
          <template v-if="hasReviewsTags">
            <p class="mb-0 mt-2">
              {{ $t("message.reviews.reviewsWith") }}
            </p>
            <v-radio-group v-model="reviewsTags.searchType" class="mt-0">
              <v-radio
                :label="
                  $t(
                    `message.reviewsTagsSearchTypes.${reviewsTags.searchTypes.INCLUDE_ALL_TAGS}`
                  )
                "
                :value="reviewsTags.searchTypes.INCLUDE_ALL_TAGS"
              ></v-radio>
              <v-radio
                :label="
                  $t(
                    `message.reviewsTagsSearchTypes.${reviewsTags.searchTypes.INCLUDE_ANY_TAG}`
                  )
                "
                :value="reviewsTags.searchTypes.INCLUDE_ANY_TAG"
              ></v-radio>
              <v-radio
                :label="$t(`message.reviewsTagsSearchTypes.noCategories`)"
                :value="reviewsTags.searchTypes.NO_TAGS"
              ></v-radio>
            </v-radio-group>
          </template>
          <InfoBox
            v-else
            :text="$t('message.reviews.tags.noTagsFilterDialogText')"
          />
        </header>

        <v-list class="tags-list">
          <v-list-item
            v-for="tag in sortedReviewTags"
            :key="tag.id"
            ripple
            class="list-item"
            :disabled="isNoTags"
            @click="toggleSelectedReviewTag(tag.id)"
          >
            <v-checkbox
              class="mt-0 pt-0"
              :disabled="isNoTags"
              :hideDetails="true"
              :inputValue="reviewsTags.selectedTagsIds.includes(tag.id)"
            ></v-checkbox>
            <v-list-item-content>
              <v-list-item-title>
                {{ tag.name }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-col>
    </v-row>
    <template #actions>
      <div class="d-flex justify-center flex-fill">
        <ui-button
          @click="handleClose"
          outlined
          min-width="180px"
          color="primary"
          class="mx-2"
        >
          {{ $t("message.reviews.cancel") }}
        </ui-button>
        <ui-button
          @click="handleSave"
          min-width="180px"
          color="primary"
          class="mx-2"
        >
          {{ $t("message.reviews.save") }}
        </ui-button>
      </div>
    </template>
  </ui-dialog>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import { OPEN_STATUS } from "@/enums/open-status-type";
import {
  FACILITY_STATUS,
  BING_FACILITY_STATUS
} from "@/enums/facility-status-type";
import { FACILITIES_FILTER_SEARCH_TYPES } from "@/enums/facilty-details-status.enum";
import { REVIEWS_TAGS_SEARCH_TYPE } from "@/enums/reviews.enum";
import { debounce, pluralCount } from "@/helpers/helpers";
import { facilitiesFilter } from "@/helpers/mixins/facilities-filter";
import { gtmEvent__listSelect } from "@/helpers/gtm-events";
import { GTM_LIST_SELECT_ACTION, GTM_EVENT_NAME } from "@/enums/gtm-events";
import { uniq, xor } from "lodash";

import NoData from "@/components/ui/NoData";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import Pagination from "@/components/List/Pagination";
import StarsRating from "@/components/ui/StarsRating";
import InfoBox from "@/components/ui/InfoBox.vue";
import ItemsCounter from "@/components/List/ItemsCounter";
import ReviewsCheckbox from "@/components/Filter/ReviewsCheckbox";
import { BUSINESS_TYPE } from "@/enums/business-type.enum";

export default {
  name: "FilterDialog",
  components: {
    NoData,
    SectionLoader,
    Pagination,
    StarsRating,
    InfoBox,
    ItemsCounter,
    ReviewsCheckbox,
    PostFacilitiesAlert: () => import("@/components/Posts/PostFacilitiesAlert")
  },
  mixins: [facilitiesFilter],

  props: {
    showAlert: {
      type: Boolean,
      default: false
    },
    showFacilities: {
      type: Boolean,
      default: true
    },
    showCities: {
      type: Boolean,
      default: true
    },
    showFacilitiesGroups: {
      type: Boolean,
      default: true
    },
    showRatings: {
      type: Boolean,
      default: false
    },
    showCommentsOptions: {
      type: Boolean,
      default: false
    },
    showOpenStatus: {
      type: Boolean,
      default: false
    },
    showLocationStatus: {
      type: Boolean,
      default: false
    },
    showBingLocationStatus: {
      type: Boolean,
      default: false
    },
    showReviewsTags: {
      type: Boolean,
      default: false
    },
    showHasNote: {
      type: Boolean,
      default: false
    },
    showDeletedOption: {
      type: Boolean,
      default: false
    },
    showDateOptions: {
      type: Boolean,
      default: false
    },
    showReviewBoosterFilters: {
      type: Boolean,
      default: false
    },
    verifiedFacilitiesOnly: {
      type: Boolean,
      default: false
    },
    gmbAddressesIds: {
      type: Array,
      default: () => []
    },
    facilitiesIds: {
      type: Array,
      default: () => []
    },
    facilitiesGroupsIds: {
      type: Array,
      default: () => []
    },
    cities: {
      type: Array,
      default: () => []
    },
    rating: {
      type: Array,
      default: () => [5, 4, 3, 2, 1]
    },
    chosenReviewsTags: {
      type: Array,
      default: () => []
    },
    chosenOnlyComments: {
      type: Boolean,
      default: false
    },
    chosenOnlyNotes: {
      type: Boolean,
      default: false
    },
    chosenOnlyAccurate: {
      type: Boolean,
      default: false
    },
    chosenSearchType: {
      type: String
    },
    chosenOpenStatus: {
      type: Number,
      validator: value => Object.values(OPEN_STATUS).includes(value)
    },
    chosenLocationStatus: {
      type: [Number, String],
      validator: value => Object.values(FACILITY_STATUS).includes(value)
    },
    chosenBingLocationStatus: {
      type: [Number, String],
      validator: value => Object.values(BING_FACILITY_STATUS).includes(value)
    },
    chosenReviewsTagsSearchType: {
      type: Number,
      validator: value =>
        Object.values(REVIEWS_TAGS_SEARCH_TYPE).includes(value)
    },
    chosenOnlyUpdated: {
      type: Boolean,
      default: false
    },
    chosenOnlyCreated: {
      type: Boolean,
      default: false
    },
    handleClose: {
      type: Function,
      required: true
    },
    ownerOnly: {
      type: Boolean,
      default: true
    },
    predefinedGroups: {
      type: Boolean,
      default: false
    },
    tags: {
      type: Array,
      default: () => []
    },
    showAccurateReviewsFilter: {
      default: true,
      type: Boolean
    },
    selectDeletedReviews: {
      default: false,
      type: Boolean
    },
    chosenAddedToGoogle: {
      default: false,
      type: Boolean
    },
    chosenWrittenWithAI: {
      default: false,
      type: Boolean
    },
    chosenGoogleMapsClicked: {
      default: false,
      type: Boolean
    },
    disableAreaFacilities: {
      default: false,
      type: Boolean
    }
  },
  data() {
    return {
      searchTypes: FACILITIES_FILTER_SEARCH_TYPES,
      searchType: null,
      listItemHeight: 66,
      loadingList: false,
      loading: false,
      citiesSearchInputValue: "",
      facilitiesList: {
        page: 1,
        perPage: 200,
        perPageOptions: [10, 25, 50, 100, 200],
        searchInputValue: ""
      },
      facilitiesGroupsList: {
        page: 1,
        perPage: 5,
        perPageOptions: [5, 10, 25, 50, 100],
        searchInputValue: ""
      },
      citiesChecked: [],
      status: {
        open: 0,
        location: 0,
        bing: 0
      },
      availableStatuses: {
        open: [...Object.values(OPEN_STATUS)],
        location: [...Object.values(FACILITY_STATUS)],
        bing: [...Object.values(BING_FACILITY_STATUS)]
      },
      ratingChecked: [5, 4, 3, 2, 1],
      onlyComments: false,
      onlyAccurate: false,
      onlyNotes: false,
      showDeleted: false,
      onlyCreated: false,
      onlyUpdated: false,
      addedToGoogle: false,
      writtenWithAI: false,
      googleMapsClicked: false,
      reviewsTags: {
        searchTypes: REVIEWS_TAGS_SEARCH_TYPE,
        searchType: null,
        tags: [],
        selectedTagsIds: []
      },
      loaders: {
        facilities: false,
        groups: false,
        cities: false
      },
      selectedGroupsNames: []
    };
  },

  computed: {
    isNoTags() {
      return (
        this.reviewsTags.searchType === this.reviewsTags.searchTypes.NO_TAGS
      );
    },
    getAlertType() {
      if (this.searchType === this.searchTypes.FACILITIES_GROUPS) {
        return this.$t("message.posts.planning.groups");
      } else if (this.searchType === this.searchTypes.CITY) {
        return this.$t("message.posts.planning.cities");
      }

      return undefined;
    },
    ...mapState({
      currentPageFacilitiesGroups: state =>
        state.facilitiesGroups.currentPageFacilitiesGroups,
      selectedFacilitiesGroupsIds: state =>
        state.facilitiesGroups.selectedGroupsIds,
      facilitiesGroupsTotalCount: state =>
        state.facilitiesGroups.groupsTotalCount
    }),
    ...mapGetters({
      selectedFacilitiesGroupsCount: "facilitiesGroups/selectedGroupsCount",
      isAllFacilitiesGroupsSelected:
        "facilitiesGroups/isAllFacilitiesGroupsSelected",
      allFacilitiesAccess: "company/allFacilitiesAccess"
    }),
    filteredCities() {
      const query = this.citiesSearchInputValue || "";
      return this.facilitiesMixin__cities.filter(city =>
        city.toLowerCase().includes(query.toLowerCase())
      );
    },
    filteredFacilitiesGroups() {
      const query = this.facilitiesGroupsList.searchInputValue || "";
      return this.currentPageFacilitiesGroups.filter(group =>
        group.name.toLowerCase().includes(query.toLowerCase())
      );
    },
    isAllCitiesSelected() {
      return this.filteredCities.every(city =>
        this.citiesChecked.includes(city)
      );
    },
    hasReviewsTags() {
      return (
        Array.isArray(this.reviewsTags.tags) && this.reviewsTags.tags.length > 0
      );
    },
    sortedReviewTags() {
      const unsorted = [...this.reviewsTags.tags];
      return this.hasReviewsTags
        ? unsorted.sort((a, b) => a.name.localeCompare(b.name))
        : [];
    },
    panelsVisibility() {
      return {
        left: true,
        middle: this.showRatings || this.showCommentsOptions,
        right: this.showReviewsTags
      };
    },
    panelsColsSpan() {
      const hasMiddleRow = this.panelsVisibility.middle;
      const hasRightRow = this.panelsVisibility.right;

      return {
        left:
          !hasMiddleRow && !hasRightRow
            ? "12"
            : hasMiddleRow && hasRightRow
            ? "6"
            : "8",
        middle: hasMiddleRow ? (hasRightRow ? "3" : "4") : "0",
        right: hasRightRow ? (hasMiddleRow ? "3" : "4") : "0"
      };
    },
    listsHeights() {
      const otherContentHeight = 488;

      let listItemsCount = Math.floor(
        (window.innerHeight - otherContentHeight - this.listItemHeight) /
          this.listItemHeight
      );
      listItemsCount = listItemsCount <= 0 ? 1 : listItemsCount; // show at least one item

      return {
        facilities: this.listItemHeight * listItemsCount,
        cities: this.listItemHeight * (listItemsCount + 1),
        facilitiesGroups: this.listItemHeight * listItemsCount
      };
    }
  },

  watch: {
    "reviewsTags.searchType": {
      handler(value) {
        if (value === this.reviewsTags.searchTypes.NO_TAGS) {
          this.reviewsTags.selectedTagsIds = [];
        }
      }
    },
    searchType: {
      handler(value) {
        if (
          value === this.searchTypes.FACILITIES &&
          !this.facilitiesMixin__totalCount
        ) {
          this.initFacilities();
        }
        if (
          value === this.searchTypes.CITY &&
          !this.facilitiesMixin__cities.length
        ) {
          this.initCities();
        }
        if (
          value === this.searchTypes.FACILITIES_GROUPS &&
          !this.facilitiesGroupsTotalCount
        ) {
          this.initFacilityGroups();
        }
      }
    }
  },

  async created() {
    this.loading = true;
    if (this.$route.query.facility_group) {
      this.setSelectedGroupsIds([+this.$route.query.facility_group]);
    }
    if (this.$route.query.groups) {
      this.setSelectedGroupsIds(
        this.$route.query.groups.split(",").map(item => +item) ||
          +this.$route.query.groups
      );
    }

    if (this.showReviewsTags) {
      this.setReviewsTags();
    }
    if (this.chosenSearchType) {
      this.searchType = this.chosenSearchType;
    } else {
      if (this.showFacilities) {
        this.searchType = this.searchTypes.FACILITIES;
      } else if (this.showCities) {
        this.searchType = this.searchTypes.CITY;
      } else if (this.showFacilitiesGroups) {
        this.searchType = this.searchTypes.FACILITIES_GROUPS;
      }
    }
    if (this.showRatings && this.rating.length) {
      this.ratingChecked = this.rating;
    }

    if (this.showCommentsOptions) {
      this.onlyComments = this.chosenOnlyComments;
      this.onlyAccurate = this.chosenOnlyAccurate;

      if (this.showHasNote) {
        this.onlyNotes = this.chosenOnlyNotes;
      }
    }

    if (this.showOpenStatus) {
      this.status.open = this.chosenOpenStatus;
    }

    if (this.showLocationStatus) {
      this.status.location = this.chosenLocationStatus;
    }

    if (this.showBingLocationStatus) {
      this.status.bing = this.chosenBingLocationStatus;
    }

    if (this.showReviewsTags && this.hasReviewsTags) {
      this.reviewsTags.searchType = this.chosenReviewsTagsSearchType;
      this.reviewsTags.selectedTagsIds = [...this.chosenReviewsTags];
      if (this.$route.query.tag_id) {
        this.reviewsTags.selectedTagsIds.push(+this.$route.query.tag_id);
      }
    }

    if (this.showDeletedOption) {
      this.showDeleted = this.selectDeletedReviews;
    }

    if (this.showDateOptions) {
      this.onlyUpdated = this.chosenOnlyUpdated;
      this.onlyCreated = this.chosenOnlyCreated;
    }

    if (this.showReviewBoosterFilters) {
      this.addedToGoogle = this.chosenAddedToGoogle;
      this.writtenWithAI = this.chosenWrittenWithAI;
      this.googleMapsClicked = this.chosenGoogleMapsClicked;
    }

    this.loading = false;
  },

  methods: {
    ...mapActions({
      fetchGroupFacilities: "facilitiesGroups/fetchGroupFacilities",
      fetchAllFacilitiesGroupsIds: "facilitiesGroups/fetchFacilitiesGroupsIds",
      toggleFacilitiesGroup: "facilitiesGroups/toggleFacilitiesGroup",
      toggleAllFacilitiesGroups: "facilitiesGroups/toggleAllFacilitiesGroups",
      setSelectedGroupsIds: "facilitiesGroups/setSelectedGroupsIds"
    }),
    isFacilitySelected(item) {
      return this.facilitiesMixin__selectedFacilities.some(
        ({ id }) => id === item.id && !this.isItemDisabled(item)
      );
    },
    isItemDisabled(item) {
      return (
        this.disableAreaFacilities && item.businessType === BUSINESS_TYPE.AREA
      );
    },
    checkIsGroupSelected(item) {
      return this.selectedFacilitiesGroupsIds.includes(item.id);
    },
    async initFacilities() {
      if (this.showFacilities) {
        this.loaders.facilities = true;
        await this.facilitiesMixin__initFacilities({
          perPage: this.facilitiesList.perPage,
          isOwner: this.ownerOnly,
          isVerified: this.verifiedFacilitiesOnly,
          ...(this.status.open != OPEN_STATUS.ANY && {
            openStatus: this.status.open
          })
        });

        if (this.gmbAddressesIds.length) {
          this.facilitiesMixin__selectFacilitiesByGmbAddressIds(
            this.gmbAddressesIds
          );
        } else if (this.facilitiesIds.length) {
          this.facilitiesMixin__selectFacilitiesByFacilitiesIds(
            this.facilitiesIds
          );
        } else {
          this.facilitiesMixin__selectFacilities(
            this.$route.query.facility
              ? [{ id: +this.$route.query.facility }]
              : undefined
          );
        }

        this.loaders.facilities = false;
      }
    },

    async initCities() {
      if (this.showCities) {
        this.loaders.cities = true;

        this.citiesChecked = this.$route.query.city
          ? [this.$route.query.city]
          : [...this.cities];

        await this.fetchCities();

        this.loaders.cities = false;
      }
    },

    async initFacilityGroups() {
      if (this.showFacilitiesGroups) {
        this.loaders.groups = true;

        await Promise.all([
          this.fetchFacilitiesGroups(),
          this.fetchAllFacilitiesGroupsIds()
        ]);

        if (this.predefinedGroups) {
          this.setSelectedGroupsIds(this.facilitiesGroupsIds);
        }

        this.loaders.groups = false;
      }
    },

    handleToggleAllCurrentPageFacilities() {
      this.facilitiesMixin__toggleAllCurrentPageFacilities();

      gtmEvent__listSelect({
        numSelected: this.facilitiesMixin__facilitiesIds
          ? this.facilitiesMixin__facilitiesIds.length
          : null,
        component: "locations-filter-dialog",
        action: this.facilitiesMixin__isAllCurrentPageFacilitiesSelected
          ? GTM_LIST_SELECT_ACTION.CHECK_ALL_ON_PAGE
          : GTM_LIST_SELECT_ACTION.UNCHECK_ALL_ON_PAGE,
        type: "facilities"
      });
    },

    handleToggleAllFacilities() {
      this.facilitiesMixin__toggleAllFacilities();

      gtmEvent__listSelect({
        numSelected: this.facilitiesMixin__facilitiesIds
          ? this.facilitiesMixin__facilitiesIds.length
          : null,
        component: "locations-filter-dialog",
        action: this.facilitiesMixin__isAllFacilitiesSelected
          ? GTM_LIST_SELECT_ACTION.CHECK_ALL
          : GTM_LIST_SELECT_ACTION.UNCHECK_ALL,
        type: "facilities"
      });
    },

    handleToggleFacility(item) {
      this.facilitiesMixin__toggleFacility(item);

      gtmEvent__listSelect({
        numSelected: this.facilitiesMixin__facilitiesIds
          ? this.facilitiesMixin__facilitiesIds.length
          : null,
        component: "locations-filter-dialog",
        action: this.facilitiesMixin__selectedFacilities.some(
          ({ id }) => id === item.id
        )
          ? GTM_LIST_SELECT_ACTION.CHECK
          : GTM_LIST_SELECT_ACTION.UNCHECK,
        type: "facilities"
      });
    },

    handleToggleAllFacilitiesGroups(check) {
      this.toggleAllFacilitiesGroups();
      if (check) {
        this.selectedGroupsNames = uniq([
          ...this.filteredFacilitiesGroups.map(({ name, id }) => {
            return { name, id };
          })
        ]);
      } else {
        this.selectedGroupsNames = [];
      }
      gtmEvent__listSelect({
        numSelected: this.selectedFacilitiesGroupsIds
          ? this.selectedFacilitiesGroupsIds.length
          : null,
        component: "locations-filter-dialog",
        action: this.isAllFacilitiesGroupsSelected
          ? GTM_LIST_SELECT_ACTION.CHECK_ALL
          : GTM_LIST_SELECT_ACTION.UNCHECK_ALL,
        type: "facilities-groups"
      });
    },

    handleToggleFacilitiesGroup(id, name) {
      this.toggleFacilitiesGroup(id);

      const index = this.selectedGroupsNames.findIndex(
        group => group.id === id
      );
      if (index !== -1) {
        this.selectedGroupsNames.splice(index, 1);
      } else {
        this.selectedGroupsNames.push({ id, name });
      }
      gtmEvent__listSelect({
        numSelected: this.selectedFacilitiesGroupsIds
          ? this.selectedFacilitiesGroupsIds.length
          : null,
        component: "locations-filter-dialog",
        action: this.selectedFacilitiesGroupsIds.includes(id)
          ? GTM_LIST_SELECT_ACTION.CHECK
          : GTM_LIST_SELECT_ACTION.UNCHECK,
        type: "facilities-groups"
      });
    },

    async handleFacilitiesPaginationChange({ page, perPage }) {
      this.facilitiesList.page = page;
      this.facilitiesList.perPage = perPage;
      this.loadingList = true;
      await this.fetchFacilities();
      this.loadingList = false;
    },

    async handleFacilitiesGroupsPaginationChange({ page, perPage }) {
      this.facilitiesGroupsList.page = page;
      this.facilitiesGroupsList.perPage = perPage;
      this.loadingList = true;
      await this.fetchFacilitiesGroups();
      this.loadingList = false;
    },

    handleSelectAllCities(check) {
      if (check) {
        this.citiesChecked = uniq([
          ...this.citiesChecked,
          ...this.filteredCities
        ]);
      } else {
        this.citiesChecked = this.citiesChecked.filter(
          city => !this.filteredCities.includes(city)
        );
      }
      gtmEvent__listSelect({
        numSelected: this.citiesChecked ? this.citiesChecked.length : null,
        component: "locations-filter-dialog",
        action: this.isAllCitiesSelected
          ? GTM_LIST_SELECT_ACTION.CHECK_ALL_ON_PAGE
          : GTM_LIST_SELECT_ACTION.UNCHECK_ALL_ON_PAGE,
        type: "cities"
      });
    },

    toggleSelectedCity(city) {
      const index = this.citiesChecked.findIndex(
        checkedCity => checkedCity === city
      );
      if (index !== -1) {
        this.citiesChecked.splice(index, 1);
      } else {
        this.citiesChecked.push(city);
      }

      gtmEvent__listSelect({
        numSelected: this.citiesChecked ? this.citiesChecked.length : null,
        component: "locations-filter-dialog",
        action:
          index === -1
            ? GTM_LIST_SELECT_ACTION.CHECK
            : GTM_LIST_SELECT_ACTION.UNCHECK,
        type: "cities"
      });
    },

    toggleSelectedRating(rate) {
      this.ratingChecked = xor(this.ratingChecked, [rate]);
    },

    onAllRatingsSelectChange(checked) {
      checked
        ? (this.ratingChecked = [1, 2, 3, 4, 5])
        : (this.ratingChecked = []);
    },

    setSelectedTags(selectedTags) {
      this.reviewsTags.selectedTagsIds = [...selectedTags];
    },

    toggleSelectedReviewTag(tagId) {
      if (this.reviewsTags.selectedTagsIds.includes(tagId)) {
        this.reviewsTags.selectedTagsIds = [
          ...this.reviewsTags.selectedTagsIds.filter(id => id !== tagId)
        ];
      } else {
        this.reviewsTags.selectedTagsIds.push(tagId);
      }
    },

    handleSave() {
      let gmbAddressesIds = [];
      let facilitiesIds = [];
      let cities = [];
      let facilitiesGroupsIds = [];

      const gtmEventParams = {};

      if (this.searchType === this.searchTypes.FACILITIES) {
        if (this.loaders.facilities) {
          gmbAddressesIds = this.gmbAddressesIds;
          facilitiesIds = this.facilitiesIds;
        } else {
          gmbAddressesIds = this.facilitiesMixin__gmbAddressesIds;
          facilitiesIds = this.facilitiesMixin__facilitiesIds;
        }
      }
      if (this.searchType === this.searchTypes.CITY) {
        cities = this.loaders.cities ? this.cities : this.citiesChecked;
      }
      if (this.searchType === this.searchTypes.FACILITIES_GROUPS) {
        facilitiesGroupsIds = this.loaders.groups
          ? this.facilitiesGroupsIds
          : this.selectedFacilitiesGroupsIds;
      }

      if (this.showFacilities) {
        this.$emit("setGmbAddressesIds", gmbAddressesIds);
        this.$emit("setFacilitiesIds", facilitiesIds);

        if (facilitiesIds.length > 0) {
          gtmEventParams.locations_num = facilitiesIds.length;
        }
      }

      if (this.showCities) {
        this.$emit("setCities", cities);

        if (cities.length > 0) {
          gtmEventParams.cities_num = cities.length;
        }
      }

      if (this.showFacilitiesGroups) {
        this.$emit("setFacilitiesGroupsIds", facilitiesGroupsIds);
        this.$emit(
          "setFacilitiesGroupsNames",
          this.selectedGroupsNames.map(({ name }) => {
            return name;
          })
        );

        if (facilitiesGroupsIds.length > 0) {
          gtmEventParams.location_groups_num = facilitiesGroupsIds.length;
        }
      }

      if (this.showRatings) {
        this.$emit("setRatingValues", this.ratingChecked);

        gtmEventParams.rating = this.ratingChecked.length
          ? this.ratingChecked.sort((a, b) => a - b).join(",")
          : null;
      }

      if (this.showCommentsOptions) {
        this.$emit("setOnlyComments", this.onlyComments);
        this.$emit("setOnlyAccurate", this.onlyAccurate);
        if (this.showHasNote) {
          this.$emit("setOnlyNotes", this.onlyNotes);
        }

        gtmEventParams.comment = this.onlyComments;
        gtmEventParams.accurate = this.onlyAccurate ? "true" : null;
      }

      if (this.showOpenStatus) {
        this.$emit("setOpenStatus", this.status.open);

        if (this.status.open !== OPEN_STATUS.ANY) {
          const openKeyStatus = Object.keys(OPEN_STATUS).find(
            key => OPEN_STATUS[key] === this.status.open
          );

          gtmEventParams.open_status = openKeyStatus
            ? openKeyStatus.toLowerCase()
            : null;
        }
      }

      if (this.showLocationStatus) {
        this.$emit("setLocationStatus", this.status.location);

        if (this.status.location !== FACILITY_STATUS.ANY) {
          gtmEventParams.gmb_status = this.status.location;
        }
      }

      if (this.showBingLocationStatus) {
        this.$emit("setBingLocationStatus", this.status.bing);

        if (this.status.bing !== BING_FACILITY_STATUS.ANY) {
          gtmEventParams.bing_status = this.status.bing;
        }
      }

      if (this.showReviewsTags && this.hasReviewsTags) {
        this.$emit("setReviewsTagsSearchType", this.reviewsTags.searchType);
        this.$emit("setReviewsTagsIds", [...this.reviewsTags.selectedTagsIds]);
      }

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.FILTER_LOCATIONS,
        filter: { ...gtmEventParams }
      });

      this.$emit("setSearchType", this.searchType);

      if (this.showDeletedOption) {
        this.$emit("setShowDeleted", this.showDeleted);
      }

      if (this.showDateOptions) {
        this.$emit("setOnlyUpdated", this.onlyUpdated);
        this.$emit("setOnlyCreated", this.onlyCreated);
      }

      if (this.showReviewBoosterFilters) {
        this.$emit("setAddedToGoogle", this.addedToGoogle);
        this.$emit("setWrittenWithAI", this.writtenWithAI);
        this.$emit("setGoogleMapsClicked", this.googleMapsClicked);
      }

      this.$emit("change", {
        gmbAddressesIds,
        facilitiesIds,
        cities,
        facilitiesGroupsNames: this.facilitiesGroupsNames,
        facilitiesGroupsIds,
        openStatus: this.status.open,
        locationStatus: this.status.location,
        bingLocationStatus: this.status.bing,
        rating: this.ratingChecked,
        comment: this.onlyComments,
        accurate: this.onlyAccurate,
        reviewsTagsSearchType: this.reviewsTags.searchType,
        reviewsTagsIds: this.reviewsTags.selectedTagsIds,
        showDeleted: this.showDeleted,
        onlyUpdated: this.onlyUpdated,
        onlyCreated: this.onlyCreated,
        addedToGoogle: this.addedToGoogle,
        writtenWithAI: this.writtenWithAI,
        googleMapsClicked: this.googleMapsClicked
      });

      this.handleClose();
    },

    onFacilitySearch: debounce(async function() {
      this.loadingList = true;
      this.facilitiesList.page = 1;
      await this.fetchFacilities();
      this.loadingList = false;
    }, 500),

    async handleLocationStatusChange() {
      this.loadingList = true;
      await this.fetchFacilities(true);
      this.loadingList = false;
    },

    fetchFacilities(alterSelection = false) {
      const params = {
        page: this.facilitiesList.page,
        perPage: this.facilitiesList.perPage,
        query: this.facilitiesList.searchInputValue,
        isOwner: this.ownerOnly,
        isVerified: this.verifiedFacilitiesOnly,
        fetchAllFacilitiesIds: true,
        ...(this.status.open != OPEN_STATUS.ANY && {
          openStatus: this.status.open
        })
      };

      return this.facilitiesMixin__fetchFacilities(params, alterSelection);
    },

    fetchCities() {
      return this.facilitiesMixin__fetchCities(this.citiesSearchInputValue);
    },

    fetchFacilitiesGroups() {
      return this.$store.dispatch("facilitiesGroups/fetchFacilitiesGroups", {
        page: this.facilitiesGroupsList.page,
        perPage: this.facilitiesGroupsList.perPage
      });
    },

    setReviewsTags() {
      this.reviewsTags.tags = [...this.tags];
    },

    getFacilitiesCountLabel(value) {
      return `(${pluralCount("message.pluralForms.facility", value)})`;
    },

    handleOnlyUpdated() {
      this.onlyUpdated = !this.onlyUpdated;
      this.onlyCreated = false;
    },

    handleOnlyCreated() {
      this.onlyCreated = !this.onlyCreated;
      this.onlyUpdated = false;
    },
    handleGoogleMapsClicked() {
      this.googleMapsClicked = this.googleMapsClicked === true ? null : true;
    },
    handleAddedToGoogle() {
      this.addedToGoogle = this.addedToGoogle === true ? null : true;
    },
    handleWrittenWithAI() {
      this.writtenWithAI = this.writtenWithAI === true ? null : true;
    }
  }
};
</script>

<style lang="scss" scoped>
$body-height: 587px;
$tags-radios-height: 181px;

.single-panel {
  padding-right: 0 !important;
}

.statuses {
  display: flex;
  margin-bottom: $spacing;

  &__select {
    width: 50%;

    &:first-child {
      margin-right: $spacing;
    }

    &:last-child {
      margin-left: $spacing;
    }
  }
}

.wizard__list {
  border: 1px solid $gray-lighter;
}

.list-actions__select {
  max-width: 110px;
}

.list-item {
  &--all {
    border: 1px solid $gray-lighter;
    border-bottom: none;
  }

  &__name,
  &__description {
    color: $dark;
  }

  &__name {
    @include font-size(16px);
    margin-bottom: $spacing * 0.5;
  }

  &__description {
    display: flex;
    flex-wrap: wrap;
    @include font-size(14px);
    margin-bottom: 0;

    &--is-store-code {
      & > span:first-child {
        padding-right: $spacing * 0.75;
        margin-right: $spacing * 0.75;
        border-right: 1px solid $dark;
      }
    }
  }

  &__facilities-count {
    @include font-size(13px);
    margin-left: $spacing * 1.5;
    color: $text-muted;
    white-space: pre;
  }
}

.--active {
  background-color: rgba($primary, 0.25);
}

.item-menu-desc::after {
  display: none;
}

.reviews {
  display: flex;
  flex-direction: column;
  padding: 0 $spacing * 2;

  &:not(:first-child) {
    margin-top: $spacing * 0.5;
    border-top: 1px solid $light-3;
  }

  h3 {
    margin: $spacing * 2 0 $spacing;
    text-transform: uppercase;
    color: $text;
    font-weight: 500;
    @include font-size(18px);
  }
}

.tags-list {
  max-height: $body-height - $tags-radios-height;
  overflow-y: auto;
}

.right-separator {
  padding-right: 40px;
  position: relative;
}

.right-separator::after {
  content: "";
  position: absolute;
  right: 20px;
  top: 50%;
  height: 28px;
  width: 1px;
  background-color: $base-light-2;
  transform: translateY(-50%);
}

.right-separator.v-input--checkbox::after {
  right: 25px;
  top: 32%;
}

.bottom-line {
  position: relative;
}

.bottom-line::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background-color: $gray-200;
  bottom: -14px;
}

.border-l-primary {
  border-left: 1px solid $primary;
}

.col-header {
  @include font-size(18px);
  font-weight: 500;
  text-transform: uppercase;
  color: $text;
}

.max-height-100 {
  max-height: 100%;
}

.list-container {
  min-height: 150px;
}

.v-card__actions {
  padding-top: $spacing * 3;
}

.v-list-item__content {
  display: flex;
  align-items: center;
  padding-left: $spacing;
}

.v-card {
  min-height: 400px;
}

.list-wrapper {
  height: 100%;
  position: relative;
}
</style>
